package org.lennycodes.hldi.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.css.textAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.lennycodes.hldi.constants.HLDITheme
import org.lennycodes.hldi.data.TeamMember


// ---------------- STYLES ----------------

val TeamDialogBackdropStyle = CssStyle {
    base {
        Modifier
            .fillMaxSize()
            .backgroundColor(Color.rgba(0, 0, 0, 0.6f))
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("display", "flex")
                property("justify-content", "center")
                property("align-items", "center")
                property("z-index", "2000")
                property("padding", "20px")
                property("animation", "fadeIn 0.2s ease")
            }
    }
}

val TeamDialogCardStyle = CssStyle {
    base {
        Modifier
            .width(600.px)
            .maxWidth(100.percent)
            .backgroundColor(Colors.White)
            .borderRadius(20.px)
            .styleModifier {
                property("position", "relative")
                property("max-height", "85vh")
                property("overflow-y", "auto")
                property("box-shadow", "0 20px 60px rgba(0,0,0,0.3)")
                property("animation", "slideUp 0.25s ease")
            }
    }
}

val TeamDialogCloseButtonStyle = CssStyle {
    base {
        Modifier
            .styleModifier {
                property("position", "absolute")
                property("top", "16px")
                property("right", "16px")
                property("width", "36px")
                property("height", "36px")
                property("border", "none")
                property("border-radius", "50%")
                property("background", "rgba(0,0,0,0.06)")
                property("font-size", "18px")
                property("line-height", "1")
                property("color", "#333")
            }
            .cursor(Cursor.Pointer)
            .transition(
                Transition.of(
                    property = "background",
                    duration = 200.ms,
                    timingFunction = TransitionTimingFunction.Ease
                )
            )
    }

    hover {
        Modifier
            .styleModifier {
                property("background", "rgba(0,0,0,0.12)")
            }
    }
}

val TeamProfileImageStyle = CssStyle {
    base {
        Modifier
            .width(140.px)
            .height(140.px)
            .borderRadius(70.px)
            .objectFit(ObjectFit.Cover)
            .styleModifier {
                property("border", "4px solid ${HLDITheme.Colors.primary}")
            }
    }
}


// ---------------- COMPONENT ----------------

@Composable
fun TeamProfileDialog(
    member: TeamMember,
    onDismiss: () -> Unit
) {

    Div(
        attrs = TeamDialogBackdropStyle.toAttrs {

            onClick {
                onDismiss()
            }

        }
    ) {

        Div(
            attrs = Modifier.padding(32.px).then(TeamDialogCardStyle.toModifier()).toAttrs {

                onClick {
                    it.stopPropagation()
                }

            }
        ) {

            // Corner close button
            Div(
                attrs = TeamDialogCloseButtonStyle.toAttrs {

                    onClick {
                        onDismiss()
                    }

                }
            ) {
                Text("\u00D7")
            }


            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.fillMaxWidth()
            ) {

                Img(
                    src = member.imageUrl,
                    alt = member.name,
                    attrs = TeamProfileImageStyle.toAttrs()
                )

                H2(
                    attrs = {
                        style {
                            marginTop(16.px)
                            marginBottom(4.px)
                        }
                    }
                ) {
                    Text(member.name)
                }

                P(
                    attrs = {
                        style {
                            color(HLDITheme.Colors.primary)
                            fontWeight(600)
                            marginBottom(2.px)
                        }
                    }
                ) {
                    Text(member.role)
                }

                P(
                    attrs = {
                        style {
                            color(HLDITheme.Colors.mediumGray)
                            fontSize(14.px)
                        }
                    }
                ) {
                    Text(member.department)
                }

            }


            P(
                attrs = {
                    style {
                        marginTop(20.px)
                        lineHeight("1.6")
                        color(HLDITheme.Colors.text)
                        textAlign(TextAlign.Center)
                    }
                }
            ) {
                Text(member.bio)
            }


            if (
                member.email.isNotEmpty() ||
                member.facebook.isNotEmpty() ||
                member.instagram.isNotEmpty()
            ) {

                Row(
                    modifier = Modifier
                        .margin(top = 24.px)
                        .fillMaxWidth(),

                    horizontalArrangement = Arrangement.Center
                ) {

                    Row(
                        horizontalArrangement = Arrangement.spacedBy(10.px)
                    ) {

                        if (member.email.isNotEmpty()) {
                            SocialButton(
                                "Email",
                                "mailto:${member.email}"
                            )
                        }

                        if (member.facebook.isNotEmpty()) {
                            SocialButton(
                                "Facebook",
                                member.facebook
                            )
                        }

                        if (member.instagram.isNotEmpty()) {
                            SocialButton(
                                "Instagram",
                                member.instagram
                            )
                        }

                    }

                }

            }

        }

    }

}